import { useEffect, useState } from "react";
import clsx from "clsx";

import { createPropsFilter } from "../../../utils/props";
import LinearProgress from "../../Progress/Linear";
import { cssUnit } from "../../types";

import styles from "./wrapper.module.scss";
import { getColor } from "../../../scss";

import type { InputWrapperProps } from "./types";

const defaultValues: Partial<InputWrapperProps> = {
  borderColor: "neutral",
  borderThickness: 1,
  nodetail: true
};

export const filterInputWrapperProps = createPropsFilter<InputWrapperProps>(
  [
    "label",
    "after",
    "before",
    "borderColor",
    "borderThickness",
    "nodetail",
    "innerbefore",
    "innerafter",
    "errorMessage",
    "variant",
    "loading"
  ],
  defaultValues
);

const InputWrapper = (props: Readonly<InputWrapperProps>) => {
  const {
    label,
    after,
    before,
    borderColor = "neutral-300",
    borderThickness = 1,
    errorMessage,
    innerafter,
    innerbefore,
    children,
    className,
    nodetail = true,
    inputRef,
    variant,
    focused = false,
    id,
    height,
    hasValue: hasValueProp = false,
    loading
  } = props;
  const [focus, setFocus] = useState(focused);
  const [hasValue, setHasValue] = useState(hasValueProp);

  useEffect(() => {
    setHasValue(hasValueProp);
  }, [hasValueProp]);

  useEffect(() => {
    setFocus(focused);
  }, [focused]);

  useEffect(() => {
    const onFocus = () => {
      setFocus(true);
    };
    inputRef?.current?.addEventListener("focus", onFocus);
    const onBlur = () => {
      setFocus(false);
    };
    inputRef?.current?.addEventListener("blur", onBlur);
    const onChange = (e) => {
      setHasValue(!!e.target.value);
    };
    inputRef?.current?.addEventListener("change", onChange);

    setHasValue(!!inputRef?.current.value);
    return () => {
      inputRef?.current?.removeEventListener("focus", onFocus);
      inputRef?.current?.removeEventListener("blur", onBlur);
      inputRef?.current?.removeEventListener("change", onChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const border =
    borderThickness &&
    `${cssUnit(borderThickness)} solid ${getColor(borderColor)}`;

  return (
    <div
      style={{
        height: height + "px" + " !important"
      }}
      className={clsx(
        className,
        styles["wrapper"],
        variant && styles[`wrapper--${variant}`],

        nodetail && styles["wrapper--no-detail"],
        !label && styles["wrapper--no-label"],
        (focus || hasValue) && styles["wrapper--active"]
      )}
    >
      {before && <div className={styles["wrapper__before"]}>{before}</div>}

      <section
        className={clsx(
          styles["wrapper__inner"],
          innerbefore && styles["wrapper__inner--with-before"],
          innerafter && styles["wrapper__inner--with-after"]
        )}
      >
        {label && <label htmlFor={id}>{label}</label>}

        <fieldset style={{ border }}>
          <legend>{label && <span>{label}</span>}</legend>
          {loading && (
            <LinearProgress
              className={styles["wrapper__inner__loading"]}
              color={borderColor}
            />
          )}
        </fieldset>

        {innerbefore && (
          <div className={styles["wrapper__inner__before"]}>{innerbefore}</div>
        )}

        <div className={styles["wrapper__inner__children"]}>{children}</div>

        {innerafter && (
          <div className={styles["wrapper__inner__after"]}>{innerafter}</div>
        )}
      </section>

      {after && <div className={styles["wrapper__after"]}>{after}</div>}

      {!nodetail && (
        <p className={styles["wrapper__message"]}>{errorMessage}</p>
      )}
    </div>
  );
};

export default InputWrapper;
